import type { ReactNode } from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import { fontWeights } from '@/@mui/tokens/font-weights';
import { ContentBox } from '@/components/shared/content-box';
import { PageContainer } from '@/components/shared/page-container';
import { PageHeader } from '@/components/shared/page-header';
import { StateMessage } from '@/components/shared/state-message';
import { mapResourceTypeToLabel } from '@/constants/resources/map-resource-type';
import { usePoliciesIndex } from '@/data/subsidy/policies';
import { useSetTitle } from '@/hooks/use-set-title';
import { formatDate } from '@/utils/format-date';
import { formatMoney } from '@/utils/format-money';
import { getSvg } from '@/utils/get-svg';

type PolicyItemProps = {
  label: string;
  description: ReactNode;
};

const getCycleSizeLabel = (cycle: number): string => {
  const cycles: Record<number, string> = {
    12: 'ano',
    6: 'semestre',
    3: 'trimestre',
    1: '1 mês',
  };

  return cycles[cycle] ?? `${cycle} meses`;
};

const getPeriodLabel = (months: number): string => {
  if (months === 1) return '1 mês';
  if (months === 12) return '1 ano';
  return `${months} meses`;
};

const getRequestLimitLabel = (
  maxRefunds: number,
  renewalPeriodInMonths: number
): string => {
  const periodLabel = getPeriodLabel(renewalPeriodInMonths);
  return `${maxRefunds} a cada ${periodLabel}`;
};

const PolicyItem = ({ label, description }: PolicyItemProps) => {
  const string = typeof description === 'string';
  return (
    <Stack direction={{ md: 'row' }} alignItems={{ md: 'flex-start' }}>
      <Typography width={160} fontWeight={fontWeights.fontWeightSemibold}>
        {label}
      </Typography>
      {string && (
        <Typography variant="body1" flex={1}>
          {description}
        </Typography>
      )}
      {!string && <Box flex={1}>{description}</Box>}
    </Stack>
  );
};

const EmptyState = () => (
  <StateMessage
    title="Você ainda não possui nenhuma regra para solicitação de reembolso
          associada a sua conta"
    description="As regras para solicitação de reembolso aparecerão aqui."
    image={getSvg('status_empty_refund.svg')}
  />
);

export const Policies = () => {
  useSetTitle('Políticas de reembolso');
  const { policies, isLoading } = usePoliciesIndex();
  const navigate = useNavigate();

  return (
    <PageContainer>
      <PageHeader
        title="Ver regras"
        description="Confira as políticas disponíveis para solicitar o seu reembolso, no caso de dúvidas procure o seu RH."
        backNavigationText="Meus reembolsos"
        handleBackNavigation={() => navigate('/meus-reembolsos')}
      />
      {isLoading && <Skeleton variant="rounded" width="100%" height={230} />}
      {!isLoading && policies.length === 0 && <EmptyState />}
      <Stack component="ul" direction="column" spacing={2}>
        {policies.map((policy) => {
          const amountAvailable = policy.limits.amount_available;
          const amountLimitPerCycleCents =
            policy.limits?.amount_limit_per_cycle_cents;
          const eligibleCourseTypes =
            policy?.configuration?.eligible_course_types ?? [];
          const eligibleExternalLabels =
            policy?.configuration?.eligible_external_resource_kinds?.map(
              (kind) => mapResourceTypeToLabel[kind]
            ) ?? [];
          const requestLimitInfo = policy?.request_limit_info;
          const hasRequestLimit =
            requestLimitInfo?.max_refunds_per_period &&
            requestLimitInfo?.renewal_period_in_months;

          return (
            <li key={policy.uuid} style={{ listStyle: 'none' }}>
              <ContentBox>
                <Typography variant="h2">{policy.name}</Typography>
                <Stack gap={2} mt={3}>
                  {amountAvailable && (
                    <PolicyItem
                      label="Saldo disponível"
                      description={
                        <Typography color="success.dark">
                          {formatMoney(Number(amountAvailable))}
                        </Typography>
                      }
                    />
                  )}
                  {amountLimitPerCycleCents && (
                    <PolicyItem
                      label="Ciclo do saldo"
                      description={
                        <Box flex={1}>
                          <Stack direction="row" spacing={1}>
                            <Typography color="success.dark" mb={1}>
                              {formatMoney(Number(amountLimitPerCycleCents))}
                            </Typography>
                            <Typography>
                              por{' '}
                              {getCycleSizeLabel(policy.cycle_size_in_months)}
                            </Typography>
                          </Stack>
                          <Typography>
                            Com renovação no dia{' '}
                            {formatDate(policy?.ends_cycle_at)}. O saldo não é
                            cumulativo e se não for usado até o dia da
                            renovação, será perdido.
                          </Typography>
                        </Box>
                      }
                    />
                  )}
                  <PolicyItem
                    label="Recursos elegíveis"
                    description={
                      <Box flex={1}>
                        {eligibleCourseTypes.length > 0 && (
                          <Typography
                            mb={eligibleExternalLabels.length > 0 ? 1 : 0}
                          >
                            <Typography component="span" fontWeight="semibold">
                              Itens na plataforma Galena:
                            </Typography>{' '}
                            {eligibleCourseTypes.join(', ')}.
                          </Typography>
                        )}
                        {eligibleExternalLabels.length > 0 && (
                          <Typography>
                            <Typography component="span" fontWeight="semibold">
                              Itens fora da plataforma Galena:
                            </Typography>{' '}
                            {eligibleExternalLabels.join(', ')}.
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  {hasRequestLimit && (
                    <PolicyItem
                      label="Limite de solicitações"
                      description={getRequestLimitLabel(
                        requestLimitInfo.max_refunds_per_period,
                        requestLimitInfo.renewal_period_in_months
                      )}
                    />
                  )}
                  <PolicyItem
                    label="Descrição"
                    description={policy.description}
                  />
                </Stack>
              </ContentBox>
            </li>
          );
        })}
      </Stack>
    </PageContainer>
  );
};
